import { z } from 'zod';
import { ufSchema } from './cidades.ts';
import {
  dataHoraSchema,
  paginacaoSchema,
  regimeTributarioSchema,
  respostaListaSchema,
  tipoPessoaSchema,
  uuidSchema,
} from './comum.ts';
import { contatoRespostaSchema, contatoSchema } from './contato.ts';
import { enderecoSchema, textoOpcional } from './endereco.ts';
import { ehDocumentoValido } from './documento.ts';
import { fiscalDaEmpresaRespostaSchema, fiscalDaEmpresaSchema } from './empresas-fiscal.ts';

/**
 * Empresa é uma linha de `pessoas` com `is_empresa = true` (.spec/016).
 *
 * Não existe tabela `empresas`: o cadastro é único e multipapel, então a mesma
 * pessoa pode ser empresa do grupo e fornecedora de outra empresa do grupo sem
 * virar dois registros que divergem no primeiro cadastro de endereço.
 */

/* -------------------------------------------------------------------------- */
/* Logotipo                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Os dois formatos aceitos.
 *
 * PNG pelo fundo transparente, que é como marca de empresa costuma vir do
 * designer; JPEG porque é o que sai de foto e de papel timbrado digitalizado.
 *
 * **SVG fica de fora, e não é esquecimento.** Ele é documento executável —
 * aceita `<script>` e `<foreignObject>` —, e exibi-lo num `<img>` de dentro do
 * sistema é superfície de ataque por um ganho de nitidez num quadrado de 32px
 * que ninguém pediu. WebP também fica de fora, por outro motivo: o navegador
 * gera, mas o que recebe o logotipo depois dele (relatório, impresso, planilha
 * exportada) nem sempre lê, e o formato mais novo não é o que o arquivo precisa
 * ser aqui.
 */
export const LOGOTIPO_TIPOS = ['image/png', 'image/jpeg'] as const;
export type LogotipoTipo = (typeof LOGOTIPO_TIPOS)[number];

/** Maior lado da imagem guardada, em pixels. Quem reduz é a tela. */
export const LOGOTIPO_LADO_MAX = 512;
/** Maior lado da miniatura, em pixels. */
export const LOGOTIPO_MINIATURA_LADO_MAX = 64;

/**
 * Teto do arquivo guardado, em bytes.
 *
 * **O limite é do corpo JSON, não do disco.** O logotipo viaja dentro do payload
 * da empresa, em base64 — que ocupa um terço a mais —, e o corpo que o Fastify
 * aceita por padrão é 1 MiB. Com 256 KiB de imagem sobra folga de sobra para o
 * resto do cadastro e para a miniatura, e um teto maior transformaria "salvei a
 * empresa" num erro de servidor sem campo a destacar.
 */
export const LOGOTIPO_BYTES_MAX = 256 * 1024;

/** Teto da miniatura, em bytes. Ela vai em **cada linha** da listagem. */
export const LOGOTIPO_MINIATURA_BYTES_MAX = 16 * 1024;

/**
 * `data:image/png;base64,…` — o formato em que o logotipo viaja, nos dois
 * sentidos.
 *
 * **Por que `data:` URL e não multipart.** O logotipo é campo do cadastro, e
 * quem o escolhe está no meio do formulário: com rota própria de upload ele
 * gravaria fora do "salvar", e uma empresa nova — que ainda não tem id — não
 * teria onde pôr o arquivo antes do primeiro salvamento. No corpo JSON ele segue
 * a regra que vale para todo o resto: mestre e filhos num payload só, gravados
 * na mesma transação.
 *
 * E **por que não uma rota `GET` que devolve a imagem**: o access token vive em
 * memória e vai no cabeçalho `Authorization`, que um `<img src>` não manda. A
 * imagem teria de ser buscada por `fetch` e virar `objectURL` de qualquer jeito
 * — trabalho a mais pela mesma tela.
 */
const dataUrlDeImagem = /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * Quantos bytes o arquivo tem, sem decodificá-lo.
 *
 * Base64 sempre gasta 4 caracteres por 3 bytes, então o comprimento responde
 * sozinho; o `=` do fim é preenchimento e não conta. Decodificar só para medir
 * alocaria o arquivo inteiro em memória a cada validação — e a validação roda
 * antes de qualquer decisão sobre aceitar o arquivo, que é justamente quando não
 * se quer alocá-lo.
 */
export function bytesDoDataUrl(dataUrl: string): number {
  const virgula = dataUrl.indexOf(',');
  if (virgula < 0) return 0;

  const base64 = dataUrl.slice(virgula + 1);
  const preenchimento = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;

  return Math.floor((base64.length * 3) / 4) - preenchimento;
}

/** Desmonta o `data:` URL nas duas partes que o banco guarda. */
export function partesDoDataUrl(dataUrl: string): { tipo: LogotipoTipo; base64: string } | null {
  if (!dataUrlDeImagem.test(dataUrl)) return null;

  const virgula = dataUrl.indexOf(',');
  const tipo = dataUrl.slice('data:'.length, dataUrl.indexOf(';'));

  return { tipo: tipo as LogotipoTipo, base64: dataUrl.slice(virgula + 1) };
}

/** Remonta o `data:` URL a partir do que o banco guardou. */
export function montarDataUrl(tipo: string, base64: string): string {
  return `data:${tipo};base64,${base64}`;
}

function imagemSchema(bytesMax: number, oQueE: string) {
  return z
    .string()
    .regex(dataUrlDeImagem, `Formato inválido. ${oQueE} precisa ser PNG ou JPEG.`)
    .refine(
      (valor) => bytesDoDataUrl(valor) > 0 && bytesDoDataUrl(valor) <= bytesMax,
      `${oQueE} passa do tamanho máximo de ${Math.round(bytesMax / 1024)} KB.`,
    );
}

export const logotipoSchema = imagemSchema(LOGOTIPO_BYTES_MAX, 'O logotipo');
export const logotipoMiniaturaSchema = imagemSchema(
  LOGOTIPO_MINIATURA_BYTES_MAX,
  'A miniatura do logotipo',
);

/**
 * As duas versões chegam **juntas ou nenhuma**.
 *
 * Quem reduz é o navegador de quem sobe o arquivo (`LOGOTIPO_LADO_MAX` e
 * `LOGOTIPO_MINIATURA_LADO_MAX`), porque o `apps/api` não tem biblioteca de
 * imagem e reduzir no servidor a cada leitura pagaria o custo em toda página da
 * listagem. A consequência é esta regra: miniatura sem original é uma marca que
 * não abre no cadastro, e original sem miniatura faz a listagem cair de volta no
 * arquivo grande — vinte por página.
 *
 * `null` nos dois é o normal, e é o que remove o logotipo de quem já tinha: o
 * `PUT` regrava o cadastro inteiro, e o logotipo segue a mesma regra dos filhos
 * do documento.
 */
function validarParDoLogotipo(
  dados: { logotipo?: string | null; logotipoMiniatura?: string | null },
  ctx: z.RefinementCtx,
): void {
  const temImagem = typeof dados.logotipo === 'string';
  const temMiniatura = typeof dados.logotipoMiniatura === 'string';

  if (temImagem === temMiniatura) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: [temImagem ? 'logotipoMiniatura' : 'logotipo'],
    message: 'O logotipo e a miniatura dele são gravados juntos ou nenhum dos dois.',
  });
}

const camposEmpresa = {
  tipo: tipoPessoaSchema,
  nome: z.string().trim().min(2, 'Informe a razão social.').max(200),
  /** Em PF repete o conteúdo de `nome`; o service preenche quando vier vazio. */
  nomeFantasia: z.string().trim().max(200).optional(),
  /**
   * Obrigatório na empresa, ao contrário de cliente e fornecedor: o regime
   * tributário dela decide o direito a crédito de imposto, e a apuração não
   * tem como rodar sem o documento do estabelecimento.
   */
  documento: z.string().trim().min(1, 'Informe o CNPJ.'),
  regimeTributario: regimeTributarioSchema,
  isCliente: z.boolean().prefault(false),
  isFornecedor: z.boolean().prefault(false),
  observacao: textoOpcional(2000),

  /**
   * O logotipo, ou `null` para não ter nenhum.
   *
   * Vem no payload do cadastro, e não por rota de upload — o porquê está em
   * `dataUrlDeImagem`, logo acima. Como o `PUT` regrava o cadastro inteiro,
   * mandar `null` é o que **apaga** o logotipo de quem já tinha: quem edita a
   * empresa por outro motivo tem de devolver o que leu, exatamente como faz com
   * os demais campos.
   */
  logotipo: logotipoSchema.nullable().prefault(null),
  logotipoMiniatura: logotipoMiniaturaSchema.nullable().prefault(null),

  /**
   * Os dados com que a empresa emite nota fiscal de serviço (`.spec/015`,
   * decisão 88) — ver `empresas-fiscal.ts`.
   *
   * Objeto aninhado, e não campos soltos: são onze, só fazem sentido juntos e
   * moram numa tabela própria (`empresas_fiscal`). O `.prefault({})` é o que
   * mantém o payload antigo válido — quem grava a empresa sem falar de nota
   * grava o bloco inteiro vazio, que é o estado de quem ainda não emite.
   *
   * Segue a regra do logotipo: o `PUT` regrava o cadastro inteiro, e a tela
   * devolve o que leu.
   */
  fiscal: fiscalDaEmpresaSchema.prefault({}),

  ativo: z.boolean().prefault(true),
};

function validarDocumento(
  dados: { tipo: 'PF' | 'PJ'; documento: string },
  ctx: z.RefinementCtx,
): void {
  if (ehDocumentoValido(dados.tipo, dados.documento)) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['documento'],
    message: dados.tipo === 'PF' ? 'CPF inválido.' : 'CNPJ inválido.',
  });
}

export const criarEmpresaSchema = z
  .object({ ...camposEmpresa })
  .merge(enderecoSchema)
  .merge(contatoSchema)
  .superRefine(validarDocumento)
  .superRefine(validarParDoLogotipo);

export type CriarEmpresa = z.infer<typeof criarEmpresaSchema>;

export const atualizarEmpresaSchema = criarEmpresaSchema;
export type AtualizarEmpresa = z.infer<typeof atualizarEmpresaSchema>;

/**
 * Resposta.
 *
 * `documento` volta apenas com dígitos; a máscara é aplicada na exibição por
 * `formatDocumento`, nunca gravada.
 */
export const empresaSchema = z
  .object({
    id: uuidSchema,
    tipo: tipoPessoaSchema,
    nome: z.string(),
    nomeFantasia: z.string(),
    documento: z.string().nullable(),
    regimeTributario: regimeTributarioSchema.nullable(),
    isCliente: z.boolean(),
    isFornecedor: z.boolean(),
    observacao: z.string().nullable(),

    /**
     * O logotipo inteiro — **só na leitura por id**.
     *
     * Na listagem ele vem `null` de propósito, e não por falta: vinte logotipos
     * de 256 KB seriam megabytes por página para desenhar quadrados de 32px.
     * Quem a listagem traz é a `logotipoMiniatura`. O campo é o mesmo nos dois
     * esquemas porque é o mesmo recurso, e separar `EmpresaDaLista` de `Empresa`
     * duplicaria dezenove campos para distinguir um.
     */
    logotipo: z.string().nullable(),

    /** A versão pequena. Vem na listagem **e** na leitura por id. */
    logotipoMiniatura: z.string().nullable(),

    /**
     * Os dados fiscais — **só na leitura por id**, como o logotipo inteiro.
     *
     * Na listagem vem o bloco vazio: ela não mostra nenhum destes campos, e a
     * junção a mais em vinte linhas por página seria custo sem leitor.
     */
    fiscal: fiscalDaEmpresaRespostaSchema,

    ativo: z.boolean(),
    createdAt: dataHoraSchema,
    updatedAt: dataHoraSchema,
  })
  .merge(
    enderecoSchema.extend({
      /** Desnormalizado na leitura para a listagem não pedir a cidade à parte. */
      cidadeNome: z.string().nullable(),
      cidadeUf: ufSchema.nullable(),
    }),
  )
  .merge(contatoRespostaSchema);

export type Empresa = z.infer<typeof empresaSchema>;

export const listaEmpresasSchema = respostaListaSchema(empresaSchema);
export type ListaEmpresas = z.infer<typeof listaEmpresasSchema>;

export const listarEmpresasQuerySchema = paginacaoSchema.extend({
  /** Ausente traz ativos e inativos; a listagem mostra ativos por padrão. */
  ativo: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});

export type ListarEmpresasQuery = z.infer<typeof listarEmpresasQuerySchema>;

export const COLUNAS_ORDENAVEIS_EMPRESA = ['nome', 'nomeFantasia', 'createdAt'] as const;

/* -------------------------------------------------------------------------- */
/* Vínculo de insumos em lote                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Vincular a esta empresa os insumos de grupos escolhidos.
 *
 * **O buraco que isto fecha.** Insumo só entra em solicitação, cotação e pedido
 * da empresa a que ele está vinculado (`.spec/015`, decisão 10). Os vínculos em
 * massa nascem na importação do catálogo padrão, que roda no provisionamento da
 * conta — com as empresas que existiam **naquele momento**. A empresa cadastrada
 * depois nasce sem insumo nenhum, e a saída à mão é abrir insumo por insumo num
 * cadastro que tem centenas. O mesmo vale para o grupo criado depois da
 * importação.
 *
 * **Por grupo, e não "todos".** O grupo é a unidade que o usuário reconhece —
 * "esta empresa não compra acabamento" é uma frase que ele sabe dizer, e "não
 * compra o insumo nº 217" não é. A tela marca todos por padrão, porque o caso
 * comum é a empresa que trabalha com o catálogo inteiro.
 *
 * **O ramo inteiro entra.** Escolher "Materiais" leva "Materiais › Elétrica"
 * junto: é o que a palavra "dentro" significa na árvore que o usuário está
 * olhando. Quem resolve isso é o servidor, com `WITH RECURSIVE` — mandar os
 * descendentes pela requisição seriam centenas de uuid e deixaria a regra de
 * fora para quem chama a API direto.
 *
 * É irmã de `vincularInsumosDaObraSchema`, e as duas não se fundem numa só:
 * lá o destino é **o** canteiro daquela obra, resolvido no servidor e sem campo
 * no corpo; aqui os depósitos são escolha de quem aperta o botão, porque uma
 * empresa tem quantos quiser e nada no sistema sabe em quais o material deve
 * ficar.
 */
export const vincularInsumosDaEmpresaSchema = z.object({
  /**
   * Os grupos escolhidos, **sem os descendentes**.
   *
   * A tela manda só os nós marcados de verdade: marcar "Materiais" cobre o ramo
   * abaixo, e mandar os cobertos junto seria repetir o que a recursão do
   * servidor já faz — com a diferença de que a lista da tela envelhece assim que
   * alguém cria um subgrupo, e a recursão não.
   */
  grupoIds: z.array(uuidSchema).min(1, 'Escolha ao menos um grupo de insumos.'),

  /**
   * Os depósitos **desta empresa** que também recebem os materiais do ramo.
   *
   * Opcional, e vazio por padrão na tela. Vincular à empresa é o que destrava a
   * compra; a linha de saldo no depósito é o que destrava a confirmação da nota.
   * São duas decisões diferentes, e a segunda é de quem toca o estoque: encher
   * todos os depósitos por conta própria criaria linha de saldo de cimento no
   * depósito de ferramentas, e desfazê-la é insumo por insumo.
   *
   * Depósito de outra empresa é recusado, não ignorado. A rota já diz de quem é
   * o lote no caminho, e aceitar em silêncio um id alheio gravaria saldo na
   * empresa errada numa operação que o usuário leu como "vinculei nesta aqui".
   *
   * Só **material** ganha linha de saldo. Serviço não movimenta estoque
   * (`.spec/015`, decisão 9), e fica só no vínculo com a empresa.
   */
  depositoIds: z.array(uuidSchema).prefault([]),
});

export type VincularInsumosDaEmpresa = z.infer<typeof vincularInsumosDaEmpresaSchema>;

/**
 * O que o vínculo fez, em números.
 *
 * Os dois primeiros contam **linhas gravadas**, não insumos considerados: a
 * operação é idempotente, e repeti-la devolve zeros. Zero é resposta correta e
 * frequente — quem clicou duas vezes precisa ler "nada a fazer", não um erro.
 *
 * `insumosNoRamo` existe para dar sentido aos zeros. Sem ele, "0 vínculos" não
 * se distingue de "os grupos escolhidos estão vazios", que é uma situação
 * completamente diferente e se resolve escolhendo outros grupos.
 *
 * `servicosSemDeposito` é o número que explica a diferença entre os dois
 * primeiros contadores quando há depósito escolhido — e é `0` quando não há,
 * porque aí não houve serviço "deixado de fora": ninguém pediu depósito nenhum.
 * Sem essa distinção a frase da tela acusaria uma exclusão que não aconteceu.
 */
export const resultadoVinculoDeInsumosDaEmpresaSchema = z.object({
  /** Linhas de `insumos_empresa` gravadas para esta empresa. */
  vinculosEmpresaCriados: z.number().int(),
  /** Linhas de `insumos_deposito` gravadas, somando todos os depósitos escolhidos. */
  vinculosDepositoCriados: z.number().int(),
  /** Insumos ativos encontrados nos grupos escolhidos e nos ramos abaixo deles. */
  insumosNoRamo: z.number().int(),
  /** Serviços do ramo: entraram na empresa e, de propósito, não nos depósitos. */
  servicosSemDeposito: z.number().int(),
  /** Quantos depósitos receberam material — para a confirmação nomear o alcance. */
  depositosAtendidos: z.number().int(),
  /** Nome da empresa no momento da operação, para a confirmação nomear o destino. */
  empresaNome: z.string(),
});

export type ResultadoVinculoDeInsumosDaEmpresa = z.infer<
  typeof resultadoVinculoDeInsumosDaEmpresaSchema
>;
